import type { Microsoft365Recipient } from '@dbrain/shared';
import { Button, Icons, toneOf } from '@dbrain/ui';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { recipientChips, typedRecipient, type RecipientChip } from '@dbrain/app-core/microsoft365-write';

/**
 * The people of a write card (MsSohbetOnay): a chip per recipient with the initials, the name and the address as
 * they go out, and "Şirket dışı" for an address outside the account's domain (no mockup). Düzenle (taslaksız): each
 * chip can be taken out, and a typed address added; a typed address is the person's own words.
 */
export function PersonChip({ chip, children }: { chip: RecipientChip; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <span className={chip.external ? 'm365w-chip m365w-chip--external' : 'm365w-chip'} data-testid="m365-write-chip" data-address={chip.address} data-external={chip.external || undefined}>
      <span className={`m365w-chip__avatar ui-avatar--${toneOf(chip.address.toLowerCase())}`} aria-hidden="true">
        {chip.initials}
      </span>
      {chip.name.trim() && !chip.name.includes('@') ? <span className="m365w-chip__name">{chip.label}</span> : null}
      <span className="m365w-chip__address">{chip.address}</span>
      {chip.external ? (
        <span className="m365w-chip__external" data-testid="m365-write-external">
          {t('microsoft365.chat.write.external')}
        </span>
      ) : null}
      {children}
    </span>
  );
}

export function PeopleChips({ people, flags, account }: { people: readonly Microsoft365Recipient[]; flags?: { address: string; external: boolean; replyTo?: boolean }[]; account?: string | null }) {
  return (
    <span className="m365w-chips">
      {recipientChips(people, flags, account).map((chip) => (
        <PersonChip key={chip.address.toLowerCase()} chip={chip} />
      ))}
    </span>
  );
}

/**
 * Düzenle: the chips with "çıkar" buttons and a box to add a typed address (Enter or "Ekle"). An address typed but
 * not added would not go, so the card hears of it (`onPending`) and holds "Gönder" back (correctness review).
 */
export function EditablePeople({
  label,
  people,
  onChange,
  onPending,
  flags,
  account,
  testId,
}: {
  label: string;
  people: Microsoft365Recipient[];
  onChange: (people: Microsoft365Recipient[]) => void;
  onPending?: (box: string, pending: boolean) => void;
  flags?: { address: string; external: boolean; replyTo?: boolean }[];
  account?: string | null;
  testId: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [text, setText] = useState('');
  const [invalid, setInvalid] = useState(false);
  const pending = text.trim().length > 0;
  useEffect(() => {
    onPending?.(testId, pending);
    return () => onPending?.(testId, false);
  }, [onPending, testId, pending]);
  const add = () => {
    const person = typedRecipient(text);
    if (!person) {
      setInvalid(text.trim().length > 0);
      return;
    }
    if (!people.some((p) => p.address.toLowerCase() === person.address.toLowerCase())) onChange([...people, person]);
    setText('');
    setInvalid(false);
  };
  return (
    <div className="m365w-edit__people" role="group" aria-labelledby={`${id}-label`} data-testid={testId}>
      <span id={`${id}-label`} className="m365w-edit__label">
        {label}
      </span>
      <span className="m365w-chips">
        {recipientChips(people, flags, account).map((chip, index) => (
          <PersonChip key={chip.address.toLowerCase()} chip={chip}>
            <button
              type="button"
              className="m365w-chip__remove"
              aria-label={t('microsoft365.chat.write.actions.remove', { name: chip.label })}
              onClick={() => onChange(people.filter((_, i) => i !== index))}
              data-testid="m365-write-chip-remove"
            >
              <Icons.close size={13} />
            </button>
          </PersonChip>
        ))}
      </span>
      <span className="m365w-edit__add">
        <input
          id={id}
          className="ui-input m365w-edit__input"
          type="email"
          inputMode="email"
          autoComplete="off"
          value={text}
          placeholder={t('microsoft365.chat.write.actions.addAddress')}
          aria-label={`${label}: ${t('microsoft365.chat.write.actions.addAddress')}`}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? `${id}-error` : undefined}
          onChange={(e) => {
            setText(e.target.value);
            setInvalid(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              add();
            }
          }}
          data-testid={`${testId}-input`}
        />
        <Button size="sm" variant="secondary" icon={<Icons.plus size={14} />} onClick={add} disabled={!text.trim()} data-testid={`${testId}-add`}>
          {t('microsoft365.chat.write.actions.add')}
        </Button>
      </span>
      {invalid ? (
        <span id={`${id}-error`} className="m365w-edit__error" role="alert">
          {t('microsoft365.chat.write.problems.address')}
        </span>
      ) : pending ? (
        <span className="m365w-quiet" data-testid={`${testId}-pending`}>
          {t('microsoft365.chat.write.problems.pendingAddress')}
        </span>
      ) : null}
    </div>
  );
}
